import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import loadable from '@loadable/component';

import { VIEW_OPTIONS_TILES } from '../SearchResultsPage/SRP.definitions';

//todo: for some reason while aditing exactly this file it doesn't get updated with hot reload
import { ReactComponent as WishListIcon } from '../../images/icons/wishlist_transparent.svg';
import { ReactComponent as WishListSelectedIcon } from '../../images/icons/wishlist.svg';
import { ReactComponent as GoToDetailsIcon } from '../../images/icons/expand.svg';

import styles from './Product.module.scss';
import { Link } from 'react-router-dom';
import { SwitchableFeature, useSwitchableFeatures } from 'Features/SwitchableFeature/SwitchableFeature';
import Price from '../Price/Price';
import { CART, PRODUCT_CARD_TO_PDP_LINK } from '../../features/SwitchableFeature/SwitchableFeature.definitions';
import useWishlist from '../../utils/useWishlist';
import ProductImage from '../ProductImage/ProductImage';
import ToggleAdultContent from '../ToggleAdultContent';
import useAdultContentHandlers from '../../utils/useAdultContentHandlers';
import { useCookies } from 'react-cookie';
import { handleProductCardGTMClick } from './gtmEventHandler';
import searchApi from '../../api/searchApi';

const RatingShort = loadable(() => import('../RatingShort/RatingShort'));

const ProductWrapperArticle = (props) => <article {...props} />;
const ProductWrapperLink = (props) => <Link {...props} />;

const ProductWrapper = (props) => {
  const children = props.children;
  const htmlAttributes = { ...props };
  delete htmlAttributes.children;
  delete htmlAttributes.to;

  return (
    <SwitchableFeature
      feature={PRODUCT_CARD_TO_PDP_LINK}
      //todo: check if it really makes sense to pass kinda such function instead of function component directly
      fallbackFn={() => <ProductWrapperArticle {...htmlAttributes}>{children}</ProductWrapperArticle>}
    >
      <ProductWrapperLink to={props.to} {...htmlAttributes}>
        {children}
      </ProductWrapperLink>
    </SwitchableFeature>
  );
};

const Product = React.memo(
  ({
    classList = {},
    shortDesc = '',
    id,
    url,
    target,
    rel,
    searchClickPing,
    adult,
    image,
    isSelected = false,
    name,
    onClick,
    price,
    discountPercent,
    originalPrice,
    status,
    shop,
    shopRating,
    showProductLink = true,
    view = VIEW_OPTIONS_TILES,
    isFashionView = false,
    isInViewPort,
    hideDescription = false,
    isShortName = false,
    showShopInfo = false,
    resultRank = undefined, //numeric position in search results list
    category,
    productCardOriginType,
    expired = false,
  }) => {
    const intl = useIntl();
    const [marketplaceVersion] = useSwitchableFeatures([CART]);
    const { isWishlistItem, toggleWishlistItem } = useWishlist();
    const { adultContentAllowed } = useAdultContentHandlers();
    const [cookies] = useCookies(['search_filters_applied', 'search_query']);
    const isLikedProduct = isWishlistItem(id);

    const sendProductCardGTMClickEvent = useCallback(() => {
      handleProductCardGTMClick(productCardOriginType, cookies, resultRank, id, category, name, price);
    }, [category, cookies, id, name, price, productCardOriginType, resultRank]);

    const productClickHandler = useCallback(
      async (event) => {
        sendProductCardGTMClickEvent();
        //replaces the native `ping` attribute: fire the search-click beacon explicitly
        if (searchClickPing) searchApi.searchClick(searchClickPing).catch(() => {});

        if (event?.target?.href) return;
        if (onClick) onClick(event);
      },
      [onClick, searchClickPing, sendProductCardGTMClickEvent]
    );

    const stopPropagation = (e) => e.stopPropagation();

    const handleWishlistBtnPress = (event) => {
      event.stopPropagation();
      event.preventDefault();
      toggleWishlistItem(id);
    };
    const isColumnView = view !== VIEW_OPTIONS_TILES;
    const imgLoadingAttr = isInViewPort ? 'eager' : 'lazy';

    return (
      <>
        <ProductWrapper
          to={url}
          className={classnames(styles.root, classList.root, classList.layout, {
            [styles.selected]: isSelected,
            [styles.productClothing]: isFashionView && view === VIEW_OPTIONS_TILES,
            [styles.marketplaceLayout]: marketplaceVersion,
            [styles.oldLayout]: !marketplaceVersion,
            [styles.columnLayout]: isColumnView,
          })}
          id={id}
          onClick={productClickHandler}
          data-testid="Product"
          target={target}
          rel={rel}
        >
          {expired && (
            <span className={classnames(styles.badge, styles.expired, { [styles.badgeColumnView]: isColumnView })}>
              <FormattedMessage id="product.outOfStock" defaultMessage="Out of stock" />
            </span>
          )}
          {status && discountPercent < 5 && (
            <span className={classnames(styles.new, styles.badge, { [styles.badgeColumnView]: isColumnView })}>
              <FormattedMessage id="common.new" defaultMessage="NEW" />
            </span>
          )}
          {discountPercent >= 5 && !status && (
            <span
              className={classnames(styles.discount, styles.badge, { [styles.badgeColumnView]: isColumnView })}
            >{`-${discountPercent}%`}</span>
          )}
          {discountPercent >= 5 && status && (
            <span
              className={classnames(styles.badge, styles.discountAndNew, { [styles.badgeColumnView]: isColumnView })}
            >
              <span className={styles.discount}>{`-${discountPercent}%`}</span>
              <span className={styles.new}>
                <FormattedMessage id="common.new" defaultMessage="NEW" />
              </span>
            </span>
          )}
          <ProductImage
            adult={adult}
            classList={{ root: styles.productImage, quickView: styles.quickView, adultPic: styles.adultContentPic }}
            forbiddenAdultContent={adult && !adultContentAllowed}
            imgLoadingAttr={imgLoadingAttr}
            image={image}
            title={name}
            showProductLink={showProductLink}
            url={url}
            isFashionView={isFashionView}
          />
          {adult && <ToggleAdultContent classList={{ root: styles.toggleAdultContent }} />}
          <div
            className={classnames(styles.productName, {
              [styles.shortProductName]: isShortName,
            })}
            data-testid="ProductName"
          >
            {name}
          </div>
          {!hideDescription && <div className={styles.productDescription}>{shortDesc}</div>}
          {showShopInfo && (
            <div className={styles.productShop}>
              <span className={styles.productShopName}>{shop.name}</span>
              <RatingShort title={intl.formatMessage({ id: 'product.shopRatingTitle' })} value={shopRating} />
            </div>
          )}
          <div className={styles.productPrice}>
            {price && (
              <Price
                showDiscount={discountPercent > 5}
                classList={{ root: styles.priceComponent }}
                price={price}
                previousPrice={originalPrice}
                currency={process.env.REACT_APP_WEBSITE_CURRENCY}
              />
            )}
          </div>
          {(!adult || !!adultContentAllowed) && isLikedProduct && (
            <FormattedMessage id="profile.wishlistRemove" defaultMessage="Remove from wishlist">
              {(label) => (
                <button
                  aria-label={label}
                  className={classnames(styles.wishlistBtn, { [styles.wishlistSelectedBtn]: isLikedProduct })}
                  onClick={handleWishlistBtnPress}
                >
                  <WishListSelectedIcon />
                </button>
              )}
            </FormattedMessage>
          )}
          {(!adult || !!adultContentAllowed) && !isLikedProduct && (
            <FormattedMessage id="profile.wishlistAdd" defaultMessage="Add to wishlist">
              {(label) => (
                <button
                  aria-label={label}
                  className={classnames(styles.wishlistBtn, { [styles.wishlistSelectedBtn]: isLikedProduct })}
                  onClick={handleWishlistBtnPress}
                >
                  <WishListIcon />
                </button>
              )}
            </FormattedMessage>
          )}
          {showProductLink && (
            <FormattedMessage id="product.moreInfo" defaultMessage="More info">
              {(label) => (
                <Link
                  aria-label={label}
                  className={styles.goToPdp}
                  data-testid="ProductGoToPDP"
                  onClick={stopPropagation}
                  to={url}
                  noopener="true"
                  norefferer="true"
                >
                  <GoToDetailsIcon width="0.875rem" height="0.875rem" />
                </Link>
              )}
            </FormattedMessage>
          )}
        </ProductWrapper>
      </>
    );
  }
);

Product.displayName = 'Product';
Product.propTypes = {
  id: PropTypes.string,
  showProductLink: PropTypes.bool,
  classList: PropTypes.shape({
    root: PropTypes.string,
    layout: PropTypes.string,
  }),
  isInViewPort: PropTypes.bool,
};

export default Product;
